import {
  TEDParseNumberMode,
  IParseResult,
  ParseResult,
} from '../FormatterTypes';
import {
  formatterStrParam,
  formatterBoolParamDef,
  formatterStrParamDef,
  formatterIntParamDef,
} from '../Utils/ParamUtils';
import { BaseParser, IValidateParams } from './BaseParser';
import * as R from 'ramda';
import { tryAsNumber, tryAsCurrency, tryAsInt } from '../Utils/ValueUtils';
import {
  makeNumberFormatString,
  iLimit,
  numberMultiple,
  nextFormatItem,
} from '../Utils/Other';
import { replaceAllOccurancesChar, isWhiteSpace } from '../Utils/StringUtils';
export interface IPatternResult {
  regExp: RegExp;
  validSeps: string[];
  intLimits: [number, number];
  decLimits: [number, number];
}

const numbers = '0123456789';

export abstract class TEDCustomNumberParser extends BaseParser {
  abstract mode(): TEDParseNumberMode;

  defaultParams(): string {
    switch (this.mode()) {
      case TEDParseNumberMode.pnmCurrency:
        return 'Decimals="";ValidDecimalSeps=".,";ValidThousandSeps=" ";Base="1";Unsigned="N";Format=""';
      case TEDParseNumberMode.pnmInteger:
      case TEDParseNumberMode.pnmInt64:
        return 'ValidThousandSeps=" ";Unsigned="N";Format=""';
      case TEDParseNumberMode.pnmFloat:
        return 'Decimals="";ValidDecimalSeps=".,";ValidThousandSeps=" ";Base="1";Unsigned="N";Format=""';
      case TEDParseNumberMode.pnmPercent:
        return 'Decimals="";ValidDecimalSeps=".,";ValidThousandSeps=" ";Unsigned="N";Format=""';
      case TEDParseNumberMode.pnmDegrees:
        return 'Decimals="";ValidDecimalSeps=".,";ValidThousandSeps=" ";Base="1";Unsigned="N";Format=""';
      default:
        return 'Decimals="";ValidDecimalSeps=".,";ValidThousandSeps=" ";Base="1";Unsigned="N";Format=""';
    }
  }

  getValidateParams(params: string): IValidateParams {
    const validDecimalSeps = formatterStrParamDef(
      'ValidDecimalSeps',
      params,
      this.defaultParams()
    );
    const validThousandSeps = formatterStrParamDef(
      'ValidThousandSeps',
      params,
      this.defaultParams()
    );
    const base = formatterStrParamDef('Base', params, this.defaultParams());

    const percentValid =
      this.mode() === TEDParseNumberMode.pnmPercent ||
      base.toUpperCase() === 'P';
    let separators = validDecimalSeps.concat(validThousandSeps).concat('+-');
    if (percentValid) {
      separators = separators.concat('%');
    }
    return { inputsList: numbers, separators: separators };
  }

  parse(text: string, params: string): IParseResult {
    const patternResult = this.makeRegExp(params);

    if (patternResult && patternResult.regExp) {
      const { regExp, validSeps, intLimits, decLimits } = patternResult;
      const regExpResult = regExp.exec(text);
      if (regExpResult) {
        // We still have thousand separators in the string
        let intPart = replaceAllOccurancesChar(
          regExpResult[patternPos.integerPart],
          validSeps ? validSeps : [],
          ''
        );
        regExpResult[patternPos.integerPart] = intPart;
        return this.interpretParts(regExpResult, params, intLimits, decLimits);
      } else {
        return { result: ParseResult.ERROR, value: null };
      }
    } else {
      return { result: ParseResult.ERROR_PATTERN, value: null };
    }
  }

  formatString(params: string = ''): string {
    let { result: receivedFormatStr, value: formatStr } = formatterStrParam(
      'Format',
      params,
      this.defaultParams()
    );

    if (!receivedFormatStr || formatStr === '') {
      const isUnsigned = formatterBoolParamDef(
        'Unsigned',
        params,
        this.defaultParams()
      );

      const isPercent =
        formatterStrParamDef('Base', params, this.defaultParams()) === 'P';

      let paramDecimals: number = 0;
      let decimals: number = 0;

      switch (this.mode()) {
        case TEDParseNumberMode.pnmInteger: // -10u
          formatStr = makeNumberFormatString(!isUnsigned, 10, 0, isPercent);
          break;
        case TEDParseNumberMode.pnmInt64: // -16u
          formatStr = makeNumberFormatString(!isUnsigned, 16, 0, isPercent); // MAX_SAFE_INTEGER är 16 siffror långt
          break;
        case TEDParseNumberMode.pnmFloat: // -15u.10g
          paramDecimals = formatterIntParamDef(
            'Decimals',
            params,
            this.defaultParams(),
            10
          );
          decimals = iLimit(paramDecimals, 0, 10);
          formatStr = makeNumberFormatString(
            !isUnsigned,
            15,
            decimals,
            isPercent
          );
          break;
        case TEDParseNumberMode.pnmCurrency: // -15u.2g
          if (
            numberMultiple(
              TEDParseNumberMode.pnmCurrency,
              params,
              this.defaultParams()
            ) === 1
          ) {
            paramDecimals = formatterIntParamDef(
              'Decimals',
              params,
              this.defaultParams(),
              2
            );
            decimals = iLimit(paramDecimals, 0, 4);
            formatStr = makeNumberFormatString(
              !isUnsigned,
              15,
              decimals,
              false
            );
          } else {
            paramDecimals = formatterIntParamDef(
              'Decimals',
              params,
              this.defaultParams(),
              10
            );
            decimals = iLimit(paramDecimals, 0, 10);
            formatStr = makeNumberFormatString(
              !isUnsigned,
              15,
              decimals,
              isPercent
            );
          }
          break;
        case TEDParseNumberMode.pnmPercent: // -5u.2g%
          paramDecimals = formatterIntParamDef(
            'Decimals',
            params,
            this.defaultParams(),
            2
          );
          decimals = iLimit(paramDecimals, 0, 4);
          formatStr = makeNumberFormatString(!isUnsigned, 5, decimals, true);
          break;
        case TEDParseNumberMode.pnmDegrees: // -5u.2g%
          paramDecimals = formatterIntParamDef(
            'Decimals',
            params,
            this.defaultParams(),
            2
          );
          decimals = iLimit(paramDecimals, 0, 4);
          formatStr = makeNumberFormatString(
            !isUnsigned,
            5,
            decimals,
            false,
            false,
            true
          );
          break;
        default:
          formatStr = '';
      }
    }

    return formatStr;
  }

  makeRegExp(params: string): IPatternResult | null {
    const defaultParams = this.defaultParams();
    let dSeps = '';
    let formatStr = this.formatString(params);
    let regExpString = '';
    let validSeps: string[] = [];

    let sign = 0;
    let pSign = 0;
    let decSep = false;
    let decOpt = false;
    let intMin = 0;
    let intMax = 0;
    let decMin = 0;
    let decMax = 0;

    if (formatStr !== '') {
      validSeps = formatterStrParamDef(
        'ValidThousandSeps',
        params,
        defaultParams,
        ''
      ).split('');

      dSeps = formatterStrParamDef(
        'ValidDecimalSeps',
        params,
        defaultParams,
        ''
      );

      validSeps = R.difference(validSeps, dSeps.split(''));

      let index = 0;

      while (index < formatStr.length) {
        const {
          result: ftResult,
          format: fc,
          count: fcnt,
          nextIndex,
        } = nextFormatItem(formatStr, index);
        index = nextIndex;
        if (ftResult) {
          if (isWhiteSpace(fc)) {
            continue;
          }

          switch (fc) {
            case '|':
              decOpt = true;
              break;
            case '+':
            case '-':
              if (fcnt === 1 && sign === 0) {
                sign = 1;
                if (fc === '+') {
                  sign += 2;
                }

                if (intMax > 1) {
                  sign += 4;
                }
              } else {
                return null;
              }
              break;
            case '%':
              if (fcnt === 1 && pSign === 0) {
                pSign = 1;
                if (intMax <= 1) {
                  pSign += 4;
                }
              } else {
                return null;
              }
              break;
            case '.':
            case ',':
              if (
                dSeps !== '' &&
                fcnt === 1 &&
                intMax > 1 &&
                decMax === 0 &&
                !decSep
              ) {
                decSep = true;
              } else {
                return null;
              }
              break;
            case 'u':
            case 'U':
            case 'i':
            case 'I':
              if (decMax === 0 && !decSep) {
                if (['i', 'I'].includes(fc)) {
                  intMin += fcnt;
                }
                intMax += fcnt;
              } else {
                return null;
              }
              break;
            case 'f':
            case 'F':
            case 'g':
            case 'G':
              if (intMax > 1) {
                if (['f', 'F'].includes(fc)) {
                  decMin += fcnt;
                }
                decMax += fcnt;
              } else {
                return null;
              }
              break;
            default:
              return null;
          }
        } else {
          return null;
        }
      } // end while

      if (intMax === 0 || (decSep && decMax === 0)) {
        return null;
      }

      const usedIntMin = Math.max(intMin, 0);
      if (validSeps.length === 0) {
        regExpString = `(\\d{${usedIntMin},${intMax}})`;
      } else {
        let validSepsString = validSeps.reduce(
          (prevVal: string, val: string): string => {
            return (prevVal += `${val}`);
          },
          '['
        );
        if (validSepsString !== '[') {
          validSepsString += ']';
        } else {
          validSepsString = '';
        }

        regExpString = `((?:\\d|${validSepsString}(?!${validSepsString}))*)`;
      }

      let tmpStr = '()'; // Oavsett vad vill vi ha en matching group för decimal plats (för att få % tecken efteråt, på konstant nummer)
      if (decMax > 0 && decSep) {
        const usedDecMin = Math.max(decMin, 1);
        const decOptStr = decMin === 0 || decOpt ? '?' : '';
        tmpStr = `(?:[${dSeps}](\\d{${usedDecMin},${decMax}}))${decOptStr}`;
      }

      regExpString += tmpStr;

      if (sign !== 0) {
        let tmpStr: string;
        if ((sign & 2) === 0) {
          tmpStr = '([+-]?)';
        } else {
          tmpStr = '([+-])';
        }

        if ((sign & 4) === 0) {
          regExpString = tmpStr + regExpString;
        } else {
          regExpString += tmpStr; // TODO: Det här verkar inte användas
        }
      } else {
        // We still want a matching group 1 for sign...(otherwise int part becomes matching group 1)
        regExpString = '()' + regExpString;
      }

      if (
        pSign !== 0 ||
        formatterStrParamDef('Base', params, defaultParams, '1') === 'P' ||
        this.mode() === TEDParseNumberMode.pnmPercent
      ) {
        let tmpStr = '(%)?';
        if ((pSign & 4) !== 0) {
          regExpString = tmpStr + regExpString; // TODO: Det här verkar inte användas
        } else {
          regExpString += tmpStr;
        }
      }

      regExpString = '^' + regExpString + '$';
    }

    return {
      regExp: new RegExp(regExpString, 'g'),
      validSeps,
      intLimits: [intMin, intMax],
      decLimits: [decMin, decMax],
    };
  }

  interpretParts(
    texts: string[],
    params: string,
    intLimits: [number, number],
    decLimits: [number, number]
  ): IParseResult {
    let result = ParseResult.OK;
    let value = null;
    const intPart = texts[patternPos.integerPart];
    const decPart = texts[patternPos.decimalPart] || '';
    const signPart = texts[patternPos.signPart];
    const percentPart = texts[patternPos.percentPart] || '';

    if (intPart.length > intLimits[1]) {
      return {
        value: null,
        result: ParseResult.ERROR_PATTERN,
      };
    }

    if (intPart !== '' || decPart !== '') {
      switch (this.mode()) {
        case TEDParseNumberMode.pnmInt64:
        case TEDParseNumberMode.pnmInteger:
          if (decPart === '' && percentPart === '') {
            let asInteger = tryAsInt(intPart);
            if (asInteger !== null) {
              if (signPart === '-') {
                asInteger = -asInteger;
              }
              value = asInteger;
            } else {
              result = ParseResult.ERROR;
            }
          } else {
            result = ParseResult.ERROR;
          }
          break;
        case TEDParseNumberMode.pnmFloat:
        case TEDParseNumberMode.pnmPercent:
        case TEDParseNumberMode.pnmDegrees: {
          let nr = tryAsNumber(
            intPart + '.' + decPart,
            Number.MIN_VALUE,
            Number.MAX_VALUE
          );
          if (nr !== null) {
            if (signPart === '-') {
              nr = -nr;
            }

            const nrMultiple = numberMultiple(
              this.mode(),
              params,
              this.defaultParams()
            );
            const decimals = formatterIntParamDef(
              'Decimals',
              params,
              this.defaultParams()
            );
            value = nr / nrMultiple;
            value = parseFloat(
              value.toFixed(Math.max(decimals, decLimits[1]) + 2)
            );
          } else {
            result = ParseResult.ERROR;
          }
          break;
        }
        case TEDParseNumberMode.pnmCurrency: {
          let curr = tryAsCurrency(intPart + '.' + decPart);
          if (curr !== null) {
            if (signPart === '-') {
              curr = -curr;
            }
            const nrMultiple = numberMultiple(
              this.mode(),
              params,
              this.defaultParams()
            );
            const decimals = formatterIntParamDef(
              'Decimals',
              params,
              this.defaultParams()
            );
            value = curr / nrMultiple;
            value = parseFloat(value.toFixed(Math.max(decimals, decLimits[1])));
          } else {
            result = ParseResult.ERROR;
          }
          break;
        }
      }
    }

    return {
      result,
      value,
    };
  }
}

const patternPos = {
  signPart: 1,
  integerPart: 2,
  decimalPart: 3,
  percentPart: 4,
};
